import { Box, HStack, VStack, useDisclosure } from '@chakra-ui/react';

import Button from 'components/Button';
import Icon, { IconType } from 'components/Icon';
import Text from 'components/Text';
import { AchievementsModal } from 'features/preview-profile/Achievements';
import { Achievement } from 'types/edgeFunctions';

interface RecentAchievementsProps {
  achievements: Achievement[];
  totalCompleted: number;
  total: number;
}

const RecentAchievements = ({ achievements, totalCompleted, total }: RecentAchievementsProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Show last 3 completed achievements, or first 3 locked ones if none completed
  const completed = achievements.filter((a) => a.is_completed);
  const display =
    completed.length > 0
      ? completed.slice(-3).reverse()
      : achievements.filter((a) => !a.is_completed).slice(0, 3);

  return (
    <>
      <Box
        w="100%"
        p={5}
        borderRadius="xl"
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        {/* Header */}
        <HStack justify="space-between" mb={4}>
          <Text fontSize="large" fontWeight="semibold">
            Achievements
          </Text>
          <Text fontSize="small" opacity="secondary" fontWeight="medium">
            {totalCompleted}/{total}
          </Text>
        </HStack>

        {/* Achievement list — top 3 */}
        <VStack spacing={1.5} align="stretch">
          {display.map((achievement) => (
            <HStack
              key={achievement.id}
              spacing={3}
              py={2}
              px={2.5}
              borderRadius="md"
              bg={achievement.is_completed ? 'whiteAlpha.50' : 'transparent'}
              borderLeft="2px solid"
              borderLeftColor={achievement.is_completed ? '#F6E05E' : 'whiteAlpha.100'}
            >
              <Box opacity={achievement.is_completed ? 1 : 0.25}>
                <Icon type={(achievement.type || 'Star') as IconType} size={18} />
              </Box>
              <Text
                fontSize="small"
                opacity={achievement.is_completed ? 'primary' : 'tertiary'}
                fontWeight={achievement.is_completed ? 'medium' : 'regular'}
                flex={1}
              >
                {achievement.is_completed ? achievement.name : '???'}
              </Text>
              {achievement.is_completed && (
                <Box
                  px={1.5}
                  py={0.5}
                  borderRadius="full"
                  bg="rgba(246, 224, 94, 0.1)"
                  border="1px solid rgba(246, 224, 94, 0.2)"
                >
                  <Box as="span" style={{ fontSize: '10px', fontWeight: 700, color: '#F6E05E' }}>
                    +{achievement.xp} XP
                  </Box>
                </Box>
              )}
              {!achievement.is_completed && <Icon type="SidebarLockedIcon" size={14} />}
            </HStack>
          ))}
        </VStack>

        <Button scheme="ghost" size="sm" mt={3} onClick={onOpen}>
          View All
        </Button>
      </Box>
      <AchievementsModal isOpen={isOpen} onClose={onClose} achievements={achievements} />
    </>
  );
};

export default RecentAchievements;
